import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Search, 
  Check, 
  X, 
  Clock, 
  User, 
  Building, 
  Phone,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import Avatar from '../components/common/Avatar';
import Modal from '../components/common/Modal';
import { useAppContext } from '../contexts/AppContext';
import { Guest } from '../types';

const ApprovalDashboard: React.FC = () => {
  const { guests, notifications, approveGuest, rejectGuest } = useAppContext();
  const [filteredGuests, setFilteredGuests] = useState<Guest[]>([]);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  
  const unreadNotifications = notifications.filter(n => !n.read).length;

  console.log('ApprovalDashboard - Current guests:', guests.length);
  console.log('ApprovalDashboard - Filtered guests:', filteredGuests.length);

  // Debug useEffect for guests changes
  useEffect(() => {
    console.log('ApprovalDashboard - guests changed:', guests.length, guests);
  }, [guests]);

  // Filter guests based on search and status
  useEffect(() => {
    let filtered = guests;

    if (searchTerm) {
      filtered = filtered.filter(guest =>
        guest.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.idNumber.includes(searchTerm) ||
        guest.company.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(guest => guest.status === statusFilter);
    }

    // Sort by registration time descending (newest first)
    filtered = filtered.sort((a, b) => 
      new Date(b.registrationTime).getTime() - new Date(a.registrationTime).getTime()
    );

    setFilteredGuests(filtered);
  }, [guests, searchTerm, statusFilter]);

  const handleApprove = (guestId: string) => {
    approveGuest(guestId, 'Admin');
  };

  const handleReject = (guestId: string) => {
    rejectGuest(guestId, 'Admin');
  };

  const openGuestDetail = (guest: Guest) => {
    setSelectedGuest(guest);
    setIsDetailModalOpen(true);
  };

  const getStatusBadge = (status: Guest['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="warning">Chờ duyệt</Badge>;
      case 'approved':
        return <Badge variant="success">Đã duyệt</Badge>;
      case 'rejected':
        return <Badge variant="danger">Từ chối</Badge>;
      default:
        return <Badge variant="secondary">Không xác định</Badge>;
    }
  };

  const getStatusIcon = (status: Guest['status']) => {
    switch (status) {
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-warning-500" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-success-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-danger-500" />;
      default:
        return <Clock className="w-4 h-4 text-secondary-500" />;
    }
  };

  const pendingCount = guests.filter(g => g.status === 'pending').length;
  const approvedCount = guests.filter(g => g.status === 'approved').length;
  const rejectedCount = guests.filter(g => g.status === 'rejected').length;

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-secondary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-secondary-900">
                Duyệt Đăng Ký Khách
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Bell className="w-6 h-6 text-secondary-600" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-danger-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadNotifications}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Avatar size="sm" />
                <span className="text-sm font-medium text-secondary-700">Admin</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="text-center">
            <div className="text-2xl font-bold text-secondary-900">{guests.length}</div>
            <div className="text-sm text-secondary-600">Tổng đăng ký</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-warning-600">{pendingCount}</div>
            <div className="text-sm text-secondary-600">Chờ duyệt</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-success-600">{approvedCount}</div>
            <div className="text-sm text-secondary-600">Đã duyệt</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-danger-600">{rejectedCount}</div>
            <div className="text-sm text-secondary-600">Từ chối</div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Tìm kiếm theo tên, CCCD, công ty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search size={20} />}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={statusFilter === 'all' ? 'primary' : 'ghost'}
                onClick={() => setStatusFilter('all')}
              >
                Tất cả
              </Button>
              <Button
                variant={statusFilter === 'pending' ? 'warning' : 'ghost'}
                onClick={() => setStatusFilter('pending')}
              >
                Chờ duyệt ({pendingCount})
              </Button>
              <Button
                variant={statusFilter === 'approved' ? 'success' : 'ghost'}
                onClick={() => setStatusFilter('approved')}
              >
                Đã duyệt
              </Button>
              <Button
                variant={statusFilter === 'rejected' ? 'danger' : 'ghost'}
                onClick={() => setStatusFilter('rejected')}
              >
                Từ chối
              </Button>
            </div>
          </div>
        </Card>

        {/* Guest List */}
        <div className="space-y-4">
          {filteredGuests.length === 0 ? (
            <Card className="text-center py-12">
              <div className="text-secondary-500">
                Không tìm thấy đăng ký nào phù hợp
              </div>
            </Card>
          ) : (
            filteredGuests.map((guest) => (
              <Card key={guest.id} className="hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Avatar src={guest.photo} size="lg" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-lg font-semibold text-secondary-900">
                          {guest.fullName}
                        </h3>
                        {getStatusIcon(guest.status)}
                        {getStatusBadge(guest.status)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-secondary-600">
                        <div className="flex items-center space-x-1">
                          <User size={14} />
                          <span>CCCD: {guest.idNumber}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Phone size={14} />
                          <span>{guest.phoneNumber}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Building size={14} />
                          <span>{guest.company}</span>
                        </div>
                      </div>
                      <div className="text-xs text-secondary-500 mt-1">
                        Đăng ký: {new Date(guest.registrationTime).toLocaleString('vi-VN')}
                        {guest.approvedTime && (
                          <span className="ml-4">
                            Duyệt: {new Date(guest.approvedTime).toLocaleString('vi-VN')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openGuestDetail(guest)}
                    >
                      <Eye size={16} />
                    </Button>
                    {guest.status === 'pending' && (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleApprove(guest.id)}
                        >
                          <Check size={16} />
                          Duyệt
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleReject(guest.id)}
                        >
                          <X size={16} />
                          Từ chối
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Guest Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Chi tiết đăng ký"
        size="lg"
      >
        {selectedGuest && (
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <Avatar src={selectedGuest.photo} size="xl" />
              <div>
                <h3 className="text-xl font-semibold text-secondary-900">
                  {selectedGuest.fullName}
                </h3>
                <div className="mt-1">
                  {getStatusBadge(selectedGuest.status)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Số CCCD/CMND
                </label>
                <p className="text-secondary-900">{selectedGuest.idNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Số điện thoại
                </label>
                <p className="text-secondary-900">{selectedGuest.phoneNumber}</p>
              </div>
              {selectedGuest.email && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Email
                  </label>
                  <p className="text-secondary-900">{selectedGuest.email}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Công ty
                </label>
                <p className="text-secondary-900">{selectedGuest.company}</p>
              </div>
              {selectedGuest.contactPerson && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Người cần gặp
                  </label>
                  <p className="text-secondary-900">{selectedGuest.contactPerson}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Thời gian đăng ký
                </label>
                <p className="text-secondary-900">
                  {new Date(selectedGuest.registrationTime).toLocaleString('vi-VN')}
                </p>
              </div>
            </div>

            {selectedGuest.visitReason && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Lý do viếng thăm
                </label>
                <p className="text-secondary-900">{selectedGuest.visitReason}</p>
              </div>
            )}

            {selectedGuest.status === 'pending' && (
              <div className="flex justify-end space-x-3 pt-4 border-t border-secondary-200">
                <Button
                  variant="danger"
                  onClick={() => {
                    handleReject(selectedGuest.id);
                    setIsDetailModalOpen(false);
                  }}
                >
                  <X size={16} className="mr-1" />
                  Từ chối
                </Button>
                <Button
                  variant="success"
                  onClick={() => {
                    handleApprove(selectedGuest.id);
                    setIsDetailModalOpen(false);
                  }}
                >
                  <Check size={16} className="mr-1" />
                  Duyệt đăng ký
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApprovalDashboard;
